import { useMemo, useRef, useEffect, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { X, Copy, ExternalLink, Loader2, Lock, MapPin, Globe, Megaphone, BellRing, BookmarkCheck, History, Package, Share2 } from "lucide-react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { getProduct, getAffiliateLink, getPublicAffiliateLink } from "../services/ProductsService";
import { createAnnouncement } from "../services/AnnouncementService";
import { createProductSocialPost, sendProductPush } from "../services/AdminService";
import { getRetailerDisplayName, isOnlineOnlyRetailer, type RetailerType } from "../config/retailers";
import type { Product } from "../types/Product";
import { toast } from "react-toastify";
import { getProductImageUrl } from "../utils/imageUtils";
import { getRetailerLinkLabel } from "../utils/productUrls";
import CheckStockButton from "./buttons/CheckStockButton";
import { usePageCheckStock } from "../context/CheckStockContext";
import { ProductOfferChips, ProductStatusBadge, getProductStatus, hasProductOffers } from "./ProductDealBadges";
import { getStoreById } from "../utils/storeUtils";
import { useUserContext } from "../context/UserContext";
import { useTranslation } from "../context/LanguageContext";
import { isFreeTier } from "../utils/userUtils";
import { getOpenBoxConditionLabel, getRetailerLookupSku } from "../utils/openBox";
import BarcodeComponent from "./Barcode";
import OtherStoreAvailability from "./OtherStoreAvailability";
import ProductRecommendations from "./ProductRecommendations";
import ResaleCard from "./ResaleCard";
import {
  buildPriceHistoryData,
  buildProductAnnouncementPayload,
  formatCurrency,
  getCurrentInventory,
  getCurrentPrice,
  getDiscountPercentForProduct,
  getProductDetailEntitlements,
  getProductUrl,
  getSavingsFromOnlinePrice,
  isValidCode,
} from "./ProductDetailModal.utils";
import {
  formatFirstAppeared,
  formatLastUpdated,
  formatStoreId,
  getDisplayedLastChangeTimestamp,
  getLastChangeTimestamp,
  getLastSeenTimestamp,
  getLastUpdatedTimestamp,
  getMultiBuyPriceDisplay,
} from "./ProductItem.utils";
import { ProductOfferPrice } from "./ProductOfferPrice";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import SaveButton from "./buttons/SaveButton";
import ShareDealSheet from "./ShareDealSheet";
import type { GuestDealResumeAction } from "../utils/authReturn";

interface ProductDetailModalProps {
  sku: string;
  storeId: string;
  retailer: string;
  onClose: () => void;
  scrollToChart?: boolean;
  resumeAction?: GuestDealResumeAction;
}

// Price & Stock Chart Component
interface ChartDataPoint {
  date: number;
  price: number;
  inventory: number;
}

interface PriceStockChartProps {
  data: ChartDataPoint[];
  isOnlineRetailer: boolean;
}

const PRICE_COLOR = "#7c3aed";
const STOCK_COLOR = "#10b981";

const formatPriceTick = (value: number): string =>
  Math.abs(value) < 10 && value % 1 !== 0 ? `$${value.toFixed(2)}` : `$${value.toFixed(0)}`;

function PriceStockChart({ data, isOnlineRetailer }: PriceStockChartProps) {
  // Sort data chronologically (oldest first) for the chart
  const chartData = useMemo(() => {
    return [...data]
      .sort((a, b) => a.date - b.date)
      .map((item) => ({
        ...item,
        dateStr: new Date(item.date).toLocaleDateString("en-US", { month: "short", day: "numeric" }),
      }));
  }, [data]);

  const latest = chartData[chartData.length - 1];

  // Calculate domain with padding so we don't start from 0. Sub-dollar prices
  // must not be floored to an integer or a $0.25 penny item flattens onto the axis.
  const priceDomain = useMemo((): [number, number] => {
    const prices = chartData.map((d) => d.price).filter((p) => p > 0);
    if (prices.length === 0) return [0, 100];
    const min = Math.min(...prices);
    const max = Math.max(...prices);
    const range = max - min;
    const padding = range > 0 ? range * 0.2 : max * 0.1;
    const round = (v: number) => (max < 10 ? Math.round(v * 100) / 100 : Math.round(v));
    return [Math.max(0, round(min - padding)), round(max + padding)];
  }, [chartData]);

  const stockDomain = useMemo((): [number, number] => {
    const stocks = chartData.map((d) => d.inventory);
    if (stocks.length === 0) return [0, 10];
    const min = Math.min(...stocks);
    const max = Math.max(...stocks);
    const range = max - min;
    const padding = range > 0 ? Math.max(range * 0.2, 1) : Math.max(max * 0.2, 2);
    return [Math.max(0, Math.floor(min - padding)), Math.ceil(max + padding)];
  }, [chartData]);

  // Custom tooltip
  const CustomTooltip = ({ active, payload, label }: any) => {
    if (!active || !payload?.length) return null;
    return (
      <div className="bg-white/95 dark:bg-dark-800/95 backdrop-blur border border-gray-200 dark:border-dark-600 rounded-xl shadow-xl p-2.5 text-xs">
        <p className="font-semibold text-gray-800 dark:text-dark-100 mb-1.5 border-b border-gray-100 dark:border-dark-600 pb-1">{label}</p>
        {payload.map((entry: any, i: number) => (
          <div key={i} className="flex justify-between gap-3 py-0.5">
            <span className="text-gray-600 dark:text-dark-300">{entry.name}:</span>
            <span className="font-bold" style={{ color: entry.color }}>
              {entry.name === "Price" ? formatCurrency(entry.value) : `${entry.value}`}
            </span>
          </div>
        ))}
      </div>
    );
  };

  const showDots = chartData.length <= 15;

  return (
    <div className="bg-white dark:bg-dark-800 border border-gray-100 dark:border-dark-700 rounded-xl p-3">
      <div className="flex items-center justify-between gap-2 mb-1">
        <h3 className="text-[13px] font-semibold text-gray-700 dark:text-dark-100">
          {isOnlineRetailer ? "Price History" : "Price & Inventory"}
        </h3>
        <div className="flex items-center gap-2.5 text-[11px] font-bold tabular-nums">
          <span className="flex items-center gap-1.5" style={{ color: PRICE_COLOR }}>
            <span className="w-[7px] h-[7px] rounded-full" style={{ backgroundColor: PRICE_COLOR }} />
            {latest ? formatPriceTick(latest.price) : "—"}
          </span>
          {!isOnlineRetailer && (
            <span className="flex items-center gap-1.5" style={{ color: STOCK_COLOR }}>
              <span className="w-[7px] h-[7px] rounded-full" style={{ backgroundColor: STOCK_COLOR }} />
              {latest ? `${latest.inventory} ${latest.inventory === 1 ? "unit" : "units"}` : "—"}
            </span>
          )}
        </div>
      </div>
      <ResponsiveContainer width="100%" height={172}>
        <LineChart data={chartData} margin={{ top: 8, right: isOnlineRetailer ? 8 : 2, left: -8, bottom: 0 }}>
          <defs>
            <linearGradient id="stockGradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor={STOCK_COLOR} stopOpacity={0.18} />
              <stop offset="95%" stopColor={STOCK_COLOR} stopOpacity={0} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="4 4" stroke="rgba(148, 163, 184, 0.18)" vertical={false} />
          <XAxis
            dataKey="dateStr"
            tick={{ fontSize: 10, fill: "#9ca3af" }}
            tickLine={false}
            axisLine={false}
            interval="preserveStartEnd"
            minTickGap={30}
          />
          <YAxis
            yAxisId="price"
            orientation="left"
            domain={priceDomain}
            tick={{ fontSize: 10, fill: PRICE_COLOR, fontWeight: 600 }}
            tickLine={false}
            axisLine={false}
            tickFormatter={formatPriceTick}
            width={48}
          />
          {!isOnlineRetailer && (
            <YAxis
              yAxisId="stock"
              orientation="right"
              domain={stockDomain}
              tick={{ fontSize: 10, fill: STOCK_COLOR, fontWeight: 600 }}
              tickLine={false}
              axisLine={false}
              tickFormatter={(v) => `${Math.round(v)}`}
              width={32}
            />
          )}
          <Tooltip content={<CustomTooltip />} />
          {/* isAnimationActive={false}: recharts draws its entrance animation by
              overwriting strokeDasharray, which both hides the line when the
              animation is interrupted (mounting inside this modal) and destroys
              the dashed style on the inventory series. */}
          {!isOnlineRetailer && (
            <Line
              yAxisId="stock"
              type="monotone"
              dataKey="inventory"
              name="Inventory"
              stroke={STOCK_COLOR}
              strokeWidth={2}
              strokeDasharray="5 3"
              isAnimationActive={false}
              dot={showDots ? { fill: STOCK_COLOR, strokeWidth: 0, r: 2.5 } : false}
              activeDot={{ fill: STOCK_COLOR, strokeWidth: 2, stroke: "#fff", r: 4 }}
            />
          )}
          <Line
            yAxisId="price"
            type="monotone"
            dataKey="price"
            name="Price"
            stroke={PRICE_COLOR}
            strokeWidth={2.5}
            isAnimationActive={false}
            dot={showDots ? { fill: PRICE_COLOR, strokeWidth: 0, r: 3 } : false}
            activeDot={{ fill: PRICE_COLOR, strokeWidth: 2, stroke: "#fff", r: 5 }}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

function Chip({ icon, children, tone }: { icon?: ReactNode; children: ReactNode; tone: "blue" | "purple" }) {
  const tones = {
    blue: "bg-blue-50 text-blue-700 dark:bg-blue-900/25 dark:text-blue-300",
    purple: "bg-purple-50 text-purple-700 dark:bg-purple-900/25 dark:text-purple-300",
  } as const;
  return (
    <span className={`inline-flex min-w-0 items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold ${tones[tone]}`}>
      {icon}
      <span className="truncate">{children}</span>
    </span>
  );
}

export default function ProductDetailModal({
  sku,
  storeId,
  retailer,
  onClose,
  scrollToChart = false,
  resumeAction,
}: ProductDetailModalProps) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useUserContext();
  const isAdmin = user?.admin === true;
  const isFreeUser = isFreeTier(user);
  // SKU / Item # / UPC are a paid feature — hidden from guests and free accounts.
  const canViewProductCodes = !!user && !isFreeUser;
  const isHomeDepot = retailer === "home_depot";
  const isTarget = retailer === "target";
  const isDollarGeneral = retailer === "dollar_general";
  const isBestBuy = retailer === "bestbuy";
  const isCostco = retailer === "costco";
  const isTractorSupply = retailer === "tractor_supply";
  const isLocalClearance = isTractorSupply || retailer === "northern_tool" || retailer === "samsclub";
  const isOnlineRetailer = isOnlineOnlyRetailer(retailer) || ((retailer === "walmart" || retailer === "target") && storeId === "online");
  const showAsOnline = isOnlineRetailer || storeId === "online";
  const detailEntitlements = getProductDetailEntitlements({
    isFreeUser,
    isOnline: showAsOnline,
  });
  const isFreeLocalLocked = !detailEntitlements.canLoadDetail;
  const isWalmart = retailer === "walmart" || retailer === "walmart_online";
  const storeInfoRetailer =
    !isOnlineRetailer &&
    (
      retailer === "walmart" ||
      retailer === "home_depot" ||
      retailer === "dollar_general" ||
      retailer === "target" ||
      retailer === "bestbuy" ||
      retailer === "costco" ||
      retailer === "lowes" ||
      retailer === "tractor_supply" ||
      retailer === "northern_tool" ||
      retailer === "samsclub"
    )
      ? retailer
      : null;
  // Crazy-deals / penny-item products carry a suffixed pseudo-store id
  // ("1517-CRAZY-DEALS"); strip it so the badge resolves the real store.
  const physicalStoreId = formatStoreId(storeId);
  const storeInfo = useMemo(
    () => (storeInfoRetailer ? getStoreById(physicalStoreId, storeInfoRetailer) : null),
    [storeInfoRetailer, physicalStoreId]
  );
  const chartRef = useRef<HTMLDivElement>(null);
  const [affiliateLoading, setAffiliateLoading] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [announcementLoading, setAnnouncementLoading] = useState(false);
  const [pushLoading, setPushLoading] = useState(false);
  const [socialPostLoading, setSocialPostLoading] = useState(false);
  const [imageExpanded, setImageExpanded] = useState(false);
  const { t } = useTranslation();
  const pageCheckStock = usePageCheckStock();

  // Close the expanded image on Escape without closing the modal behind it
  useEffect(() => {
    if (!imageExpanded) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        setImageExpanded(false);
      }
    };
    window.addEventListener("keydown", onKeyDown, true);
    return () => window.removeEventListener("keydown", onKeyDown, true);
  }, [imageExpanded]);

  const {
    data: product,
    isLoading,
    error,
  } = useQuery<Product>({
    queryKey: ["product", sku, storeId, retailer],
    queryFn: () => getProduct(sku, showAsOnline ? "online" : storeId, retailer),
    enabled: !!(sku && (storeId || showAsOnline)) && !isFreeLocalLocked,
    retry: false,
  });

  // Scroll to chart when requested
  useEffect(() => {
    if (scrollToChart && product && chartRef.current) {
      setTimeout(() => {
        chartRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 100);
    }
  }, [scrollToChart, product]);

  // Prepare table data
  const priceHistoryData = useMemo(() => buildPriceHistoryData(product), [product]);
  const currentPrice = getCurrentPrice(product);
  const isPotentialPennyItem = retailer === "dollar_general" && (product?.storeId || storeId).endsWith("-PENNY-ITEM");
  const currentInventory = getCurrentInventory(product);
  const hasInventoryCount = Boolean(product?.salesFloor?.length);
  const multiBuy = product
    ? getMultiBuyPriceDisplay(product, currentPrice, product.msrp)
    : null;
  const discountPercent = product
    ? getDiscountPercentForProduct(product.msrp, currentPrice)
    : 0;
  const savingsAmount = product
    ? getSavingsFromOnlinePrice(product.onlinePrice, currentPrice)
    : 0;
  const lastChangeTimestamp = getLastChangeTimestamp({
    priceHistory: product?.storePrice,
    backroomHistory: product?.backroom,
    salesFloorHistory: product?.salesFloor,
    lastUpdates: product?.lastUpdates,
  });
  const lastUpdatedTimestamp = getLastUpdatedTimestamp({
    priceHistory: product?.storePrice,
    backroomHistory: product?.backroom,
    salesFloorHistory: product?.salesFloor,
    lastUpdates: product?.lastUpdates,
    firstInserted: product?.firstInserted,
    lastUpdated: product?.lastUpdated,
  });
  const lastSeenTimestamp = getLastSeenTimestamp(
    product?.lastUpdates,
    product?.firstInserted || 0
  );
  const displayedLastChangeTimestamp = getDisplayedLastChangeTimestamp(
    lastChangeTimestamp,
    lastUpdatedTimestamp
  );
  const lastChangeLabel = formatLastUpdated(
    displayedLastChangeTimestamp ? [displayedLastChangeTimestamp] : [],
    { emptyLabel: "N/A" }
  );
  const lastUpdatedLabel = formatLastUpdated([lastUpdatedTimestamp], {
    emptyLabel: "N/A",
  });
  const lastSeenLabel = formatLastUpdated([lastSeenTimestamp], {
    emptyLabel: "N/A",
  });
  const pennyFirstSpottedLabel = formatFirstAppeared(product?.firstInserted || 0);
  const pennyLastRegularLabel = lastSeenLabel;

  const copyToClipboard = async (text: string, label: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${label} copied to clipboard!`);
    } catch (err) {
      toast.error("Failed to copy to clipboard");
    }
  };

  // Retailer page for this product: the public affiliate link when the product
  // has a productKey, otherwise our affiliate link (Walmart/Target) or the plain URL.
  const productLink = product ? getProductUrl({ url: product.url, retailer, storeId: product.storeId }) : "";
  const canOpenRetailer = Boolean(product?.productKey || productLink);
  const resolveRetailerLink = async (): Promise<string> => {
    if (!product) return "";
    try {
      if (product.productKey) return await getPublicAffiliateLink(product.productKey);
      if (isWalmart || retailer === "target" || retailer === "target_online") {
        return await getAffiliateLink(product.upc || product.sku, productLink, retailer);
      }
    } catch {
      // fall back to the plain retailer URL
    }
    return productLink;
  };

  const handleAffiliateClick = async (e: React.MouseEvent) => {
    e.preventDefault();
    if (affiliateLoading || !product || !canOpenRetailer) return;
    const tab = window.open("about:blank", "_blank");
    setAffiliateLoading(true);
    try {
      const url = await resolveRetailerLink();
      if (!url) tab?.close();
      else if (tab) tab.location.href = url;
      else window.location.href = url;
    } finally {
      setAffiliateLoading(false);
    }
  };

  // Share sheet with the same affiliate link the retailer button opens.
  const sharePriceLine = `${formatCurrency(currentPrice)}${discountPercent > 0 ? ` (${discountPercent}% off)` : ""} at ${getRetailerDisplayName(retailer as RetailerType)}`;
  const shareText = product ? `${product.name} for ${sharePriceLine}` : "";
  const resolveShareUrl = async () => {
    const url = await resolveRetailerLink();
    if (!url) throw new Error("No link");
    return url;
  };

  const handleAddAsAnnouncement = async () => {
    if (announcementLoading || !product || !isWalmart) return;
    setAnnouncementLoading(true);
    try {
      const productLink = getProductUrl({ url: product.url, retailer, storeId: product.storeId });
      const affiliateLink = await getAffiliateLink(product.upc || product.sku, productLink, retailer);
      await createAnnouncement(
        buildProductAnnouncementPayload({
          product,
          currentPrice,
          discountPercent,
          affiliateLink,
          imageUrl: product.image ? getProductImageUrl(product.image) : undefined,
        })
      );
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["admin-announcements"] }),
        queryClient.invalidateQueries({ queryKey: ["user-announcements"] }),
        queryClient.invalidateQueries({ queryKey: ["unseen-announcements"] }),
        queryClient.invalidateQueries({ queryKey: ["latest-announcement"] }),
      ]);
      toast.success("Announcement posted!");
    } catch {
      toast.error("Failed to create announcement");
    } finally {
      setAnnouncementLoading(false);
    }
  };

  const handleSendPush = async () => {
    if (pushLoading || !product) return;
    const audience = showAsOnline
      ? "all users with push notifications enabled and at least one retailer"
      : `users who have store #${physicalStoreId} saved and at least one retailer`;
    if (!window.confirm(`Send a push notification for "${product.name}" to ${audience}?`)) return;
    setPushLoading(true);
    try {
      const result = await sendProductPush({
        upc: product.upc || product.sku || sku,
        retailer,
        retailerLabel: getRetailerDisplayName(retailer as RetailerType),
        storeId: showAsOnline ? "online" : physicalStoreId,
        online: showAsOnline,
        productName: product.name,
        productUrl: getProductUrl({ url: product.url, retailer, storeId: product.storeId }),
        imageUrl: product.image ? getProductImageUrl(product.image) : "",
        price: currentPrice,
        msrp: product.msrp,
        discountPercent,
      });
      if (result.targeted > 0) {
        toast.success(`Push queued for ${result.targeted} user${result.targeted === 1 ? "" : "s"}`);
      } else {
        toast.info("No eligible users to notify");
      }
    } catch (err) {
      if ((err as { status?: number }).status === 409) {
        toast.warn("A push for this product is already being sent");
      } else {
        toast.error("Failed to send push");
      }
    } finally {
      setPushLoading(false);
    }
  };

  const handleCreateSocialPost = async () => {
    if (socialPostLoading || !product) return;
    const productKey = product.productKey?.trim();
    if (!productKey) {
      toast.error("This product does not have a verified social sharing link");
      return;
    }
    if (!window.confirm(`Post "${product.name}" now to Threads, X, Bluesky, and Pinterest through Nuelink?`)) return;

    setSocialPostLoading(true);
    try {
      const result = await createProductSocialPost({
        productKey,
      });

      if (result.posts.length === 4 && result.errors.length === 0) {
        toast.success("Social post sent to Threads, X, Bluesky, and Pinterest!");
      } else if (result.posts.length > 0 && result.posts.length + result.skipped.length === 4 && result.errors.length === 0) {
        toast.success(`${result.posts.length} social post${result.posts.length === 1 ? "" : "s"} sent; the rest were already posted.`);
      } else if (result.posts.length > 0) {
        toast.warn(`Social post sent to ${result.posts.length} of 4 networks; ${4 - result.posts.length} need attention.`);
      } else if (result.skipped.length === 4) {
        toast.info("This product was already posted on all four social networks.");
      } else {
        toast.error("No social posts were sent.");
      }
    } catch {
      toast.error("Failed to send the social post");
    } finally {
      setSocialPostLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex flex-col h-full">
        {/* Header Skeleton */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-dark-700 bg-white dark:bg-dark-900 sticky top-0 z-10">
          <div className="flex items-center space-x-3 flex-1 min-w-0">
            <div className="w-12 h-12 bg-gray-200 dark:bg-dark-700 rounded-lg animate-pulse"></div>
            <div className="flex-1 min-w-0 space-y-2">
              <div className="h-5 bg-gray-200 dark:bg-dark-700 rounded w-3/4 animate-pulse"></div>
              <div className="h-4 bg-gray-200 dark:bg-dark-700 rounded w-1/2 animate-pulse"></div>
            </div>
          </div>
          <div className="w-10 h-10 bg-gray-200 dark:bg-dark-700 rounded-full animate-pulse"></div>
        </div>

        {/* Content Skeleton */}
        <div className="flex-1 overflow-y-auto p-4 pb-24 space-y-4">
          {/* Product Info Cards Skeleton */}
          <div className="grid grid-cols-2 gap-3">
            {[1, 2, 3, 4].map((i) => (
              <div
                key={i}
                className="bg-gray-100 dark:bg-dark-800 border border-gray-200 dark:border-dark-700 rounded-lg p-3 space-y-2"
              >
                <div className="h-3 bg-gray-200 dark:bg-dark-700 rounded w-1/2 animate-pulse"></div>
                <div className="h-8 bg-gray-200 dark:bg-dark-700 rounded w-2/3 animate-pulse"></div>
                <div className="h-3 bg-gray-200 dark:bg-dark-700 rounded w-1/3 animate-pulse"></div>
              </div>
            ))}
          </div>

          {/* Product Codes Skeleton */}
          <div className="bg-gray-100 dark:bg-dark-800 rounded-lg p-3 space-y-3">
            <div className="h-4 bg-gray-200 dark:bg-dark-700 rounded w-1/3 animate-pulse"></div>
            <div className="flex flex-wrap gap-3">
              <div className="h-4 bg-gray-200 dark:bg-dark-700 rounded w-32 animate-pulse"></div>
              <div className="h-4 bg-gray-200 dark:bg-dark-700 rounded w-32 animate-pulse"></div>
            </div>
            <div className="flex justify-center">
              <div className="h-16 bg-gray-200 dark:bg-dark-700 rounded w-48 animate-pulse"></div>
            </div>
          </div>

          {/* Price History Table Skeleton */}
          <div className="bg-white dark:bg-dark-800 border border-gray-100 dark:border-dark-700 rounded-xl overflow-hidden">
            <div className="px-4 py-3 bg-gray-50 dark:bg-dark-900 border-b border-gray-200 dark:border-dark-700">
              <div className="h-4 bg-gray-200 dark:bg-dark-700 rounded w-24 animate-pulse"></div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 dark:bg-dark-900">
                  <tr>
                    {[1, 2, 3, 4].map((i) => (
                      <th
                        key={i}
                        className="px-4 py-2 text-left text-xs font-medium text-gray-700 dark:text-dark-400"
                      >
                        <div className="h-3 bg-gray-200 dark:bg-dark-700 rounded w-16 animate-pulse"></div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-dark-700">
                  {[1, 2, 3, 4, 5].map((i) => (
                    <tr key={i}>
                      {[1, 2, 3, 4].map((j) => (
                        <td key={j} className="px-4 py-2">
                          <div className="h-4 bg-gray-200 dark:bg-dark-700 rounded w-20 animate-pulse"></div>
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Action Button Skeleton */}
          <div className="flex flex-col sm:flex-row gap-2">
            <div className="h-10 bg-gray-200 dark:bg-dark-700 rounded-lg w-full sm:w-48 animate-pulse"></div>
          </div>
        </div>
      </div>
    );
  }

  if (isFreeLocalLocked) {
    return (
      <div className="flex flex-col h-full">
        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-dark-700 bg-white dark:bg-dark-900 sticky top-0 z-10">
          <h2 className="text-lg font-bold text-gray-900 dark:text-white">{t("products.viewDetails")}</h2>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 dark:text-dark-400 dark:hover:text-dark-200 rounded-full hover:bg-gray-100 dark:hover:bg-dark-700 transition-colors"
            aria-label="Close"
          >
            <X className="w-6 h-6" />
          </button>
        </div>
        <div className="flex-1 flex items-center justify-center p-4 sm:p-6 bg-gray-50/60 dark:bg-dark-950">
          <div className="w-full max-w-md rounded-2xl border border-gray-200 dark:border-dark-700 bg-white dark:bg-dark-900 p-5 shadow-sm">
            <div className="w-10 h-10 rounded-xl bg-primary-100 dark:bg-primary-900/40 text-primary-700 dark:text-primary-300 flex items-center justify-center mx-auto">
              <Lock className="w-5 h-5" />
            </div>
            <p className="mt-3 text-center text-sm font-semibold text-gray-900 dark:text-white">
              {t("products.accessRestricted")}
            </p>
            <p className="mt-1 text-center text-xs text-gray-600 dark:text-dark-300">
              Exact local store details are available on paid plans. Every online feed and its price history remains available on your free account.
            </p>
            <div className="mt-4 rounded-lg border border-gray-200 dark:border-dark-700 bg-gray-50 dark:bg-dark-800 p-3">
              <p className="text-[11px] font-semibold text-gray-700 dark:text-dark-200">Unlock with upgrade:</p>
              <ul className="mt-1.5 space-y-1 text-[11px] text-gray-600 dark:text-dark-300">
                <li>• Exact local store price and inventory</li>
                <li>• Store and aisle details</li>
                <li>• Advanced confidence and prediction insights</li>
              </ul>
            </div>
            <button
              onClick={() => navigate("/settings?tab=billing&upgradeSource=product_detail_lock")}
              className="mt-4 w-full px-4 py-2 rounded-lg bg-primary-600 text-white text-sm font-semibold hover:bg-primary-700 transition-colors"
            >
              Upgrade to Basic or Pro
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (error || !product) {
    return (
      <div className="flex items-center justify-center h-full min-h-[400px]">
        <div className="text-center">
          <p className="text-red-600 mb-4">{t("products.failedToLoad")}</p>
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
          >
            {t("common.close")}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center justify-between gap-3 px-3 py-2.5 sm:px-4 border-b border-gray-200 dark:border-dark-700 bg-white dark:bg-dark-900 sticky top-0 z-10">
        <div className="flex-1 min-w-0">
          <h2 className="text-[13px] font-semibold text-gray-900 dark:text-white line-clamp-2 leading-snug">
            {product.name}
          </h2>
          {canViewProductCodes && isValidCode(product.upc) && (
            <p className="text-[11px] tabular-nums text-gray-400 dark:text-dark-400 mt-0.5">
              {isHomeDepot ? "Item ID" : isTarget ? "TCIN" : "SKU"}: {product.upc}
            </p>
          )}
        </div>
        {canOpenRetailer && (
          <button
            type="button"
            onClick={() => setShareOpen(true)}
            className="flex h-8 flex-shrink-0 items-center gap-1.5 rounded-full bg-primary-50 px-3 text-xs font-semibold text-primary-700 hover:bg-primary-100 dark:bg-primary-900/30 dark:text-primary-300 dark:hover:bg-primary-900/50 transition-colors"
            aria-label="Share deal"
          >
            <Share2 className="w-3.5 h-3.5" />
            Share
          </button>
        )}
        <ShareDealSheet
          isOpen={shareOpen}
          onClose={() => setShareOpen(false)}
          title={product.name}
          text={shareText}
          priceLine={sharePriceLine}
          imageUrl={product.image ? getProductImageUrl(product.image) : undefined}
          resolveUrl={resolveShareUrl}
        />
        <button
          onClick={onClose}
          className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-gray-100 text-gray-500 hover:bg-gray-200 hover:text-gray-700 dark:bg-dark-800 dark:text-dark-400 dark:hover:bg-dark-700 dark:hover:text-dark-200 transition-colors"
          aria-label="Close"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto px-3 py-3 sm:p-4 pb-24 space-y-2.5 sm:space-y-4">
        {resumeAction && showAsOnline && (!isFreeUser || resumeAction !== "save") && (
          <div className="flex items-center justify-between gap-3 rounded-xl border border-purple-200 bg-gradient-to-r from-purple-50 to-white p-3 dark:border-purple-800/60 dark:from-purple-950/30 dark:to-dark-900">
            <div className="flex min-w-0 items-start gap-2.5">
              <span className="mt-0.5 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-purple-100 text-purple-700 dark:bg-purple-900/50 dark:text-purple-300">
                {resumeAction === "save" ? <BookmarkCheck className="h-4 w-4" /> : <History className="h-4 w-4" />}
              </span>
              <div className="min-w-0">
                <p className="text-sm font-bold text-purple-950 dark:text-purple-100">
                  {resumeAction === "save" ? "Your deal is ready to save" : "Your full price history is unlocked"}
                </p>
                <p className="mt-0.5 text-xs leading-relaxed text-purple-700 dark:text-purple-300">
                  {resumeAction === "save"
                    ? "You’re back where you left off. Save this online deal with your free account."
                    : "You’re back where you left off. The complete online timeline is below."}
                </p>
              </div>
            </div>
            {resumeAction === "save" && (
              <SaveButton
                productId={product.upc || sku}
                productName={product.name}
                productImage={product.image}
                productCategory={product.category}
                productStoreId="online"
                onlinePrice={product.onlinePrice}
                storePrice={currentPrice}
                stock={currentInventory}
                className="flex-shrink-0 !bg-rose-100 !px-3 !py-2 !font-bold !text-rose-700 hover:!bg-rose-200"
              />
            )}
          </div>
        )}

        {isFreeUser && showAsOnline && (
          <div className="rounded-lg border border-purple-200 bg-purple-50 p-3 dark:border-purple-800/60 dark:bg-purple-900/20">
            <p className="text-sm font-semibold text-purple-900 dark:text-purple-200">Full online detail included</p>
            <p className="mt-1 text-xs text-purple-700 dark:text-purple-300">Your free account includes this listing's product data and price history. Advanced confidence and prediction insights remain part of paid plans.</p>
          </div>
        )}

        {/* Hero: image + price */}
        <div className="flex items-center gap-4">
          {product.image && (
            <button
              type="button"
              onClick={() => setImageExpanded(true)}
              className="flex-shrink-0 cursor-zoom-in rounded-2xl border border-gray-200 dark:border-dark-700 bg-white p-1.5 hover:ring-2 hover:ring-primary-400 transition-shadow"
              aria-label="View larger image"
            >
              <img
                src={getProductImageUrl(product.image, "300")}
                alt={product.name}
                className="w-24 h-24 sm:w-28 sm:h-28 object-contain rounded-xl"
              />
            </button>
          )}
          <div className="min-w-0 flex-1">
            <div className="text-[10px] font-extrabold uppercase tracking-wider text-gray-400 dark:text-dark-400">
              {multiBuy ? t("products.dealPrice") : showAsOnline ? t("products.currentPrice") : t("productSearch.storePrice")}
            </div>
            {multiBuy ? (
              <div className="mt-0.5 flex flex-wrap items-baseline gap-x-2 gap-y-1">
                <ProductOfferPrice display={multiBuy} size="hero" />
              </div>
            ) : (
              <div className="text-3xl sm:text-4xl font-extrabold tracking-tight tabular-nums text-emerald-600 dark:text-emerald-400 leading-tight">
                {formatCurrency(currentPrice)}
              </div>
            )}
            {isPotentialPennyItem && (
              <span className="mt-1 inline-flex rounded-md bg-amber-100 px-2 py-0.5 text-[10px] font-extrabold uppercase tracking-wide text-amber-800 dark:bg-amber-900/40 dark:text-amber-200">
                {t("products.potentialPennyBadge")}
              </span>
            )}
            {!multiBuy && (product.msrp > currentPrice || discountPercent > 0) && (
              <div className="mt-0.5 flex flex-wrap items-center gap-2">
                {product.msrp > currentPrice && (
                  <span className="text-sm font-semibold tabular-nums text-gray-400 line-through dark:text-dark-400">
                    {formatCurrency(product.msrp)}
                  </span>
                )}
                {discountPercent > 0 && (
                  <span className="rounded-full bg-red-100 px-2 py-0.5 text-[10px] font-extrabold tracking-wide text-red-600 dark:bg-red-900/30 dark:text-red-400">
                    {t("products.percentOffLabel", { percent: String(discountPercent) })}
                  </span>
                )}
              </div>
            )}
            {!multiBuy && savingsAmount > 0 && (
              <div className="mt-0.5 text-xs font-bold text-emerald-600 dark:text-emerald-400">
                {t("products.saveAmount", { amount: formatCurrency(savingsAmount) })}
              </div>
            )}
            {/* Same status tag and offer chips as the cards (NEW, PRICE ↓, cash back, Ibotta, BOGO). */}
            {retailer !== "woot" && (getProductStatus(product) || hasProductOffers(product)) && (
              <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                <ProductStatusBadge kind={getProductStatus(product)} />
                <ProductOfferChips product={product} />
              </div>
            )}
          </div>
        </div>

        {/* Availability chips */}
        <div className="flex flex-wrap items-center gap-1.5">
          {isBestBuy && product.openBox && (
            <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2.5 py-1 text-xs font-semibold text-amber-800 dark:bg-amber-900/40 dark:text-amber-300">
              <Package className="w-3.5 h-3.5 flex-shrink-0" />
              {product.openBoxCondition
                ? t("card.openBoxCondition", { condition: getOpenBoxConditionLabel(product.openBoxCondition, (key) => t(key)) })
                : t("products.openBoxLabel")}
            </span>
          )}
          {isBestBuy && product.openBox && (product.openBoxUnits?.length || 0) > 1 && (
            <span className="text-xs text-gray-500 dark:text-dark-400">
              {t("card.openBoxAlso", {
                units: (product.openBoxUnits || []).slice(1).map((u) => `${getOpenBoxConditionLabel(u.condition, (key) => t(key))} ${formatCurrency(u.price)}`).join(" · "),
              })}
            </span>
          )}
          <Chip tone="blue" icon={<Package className="w-3.5 h-3.5 flex-shrink-0" />}>
            {isPotentialPennyItem
              ? t("products.possibleStock")
              : showAsOnline || (isLocalClearance && !hasInventoryCount) || ((isBestBuy || isCostco) && currentInventory <= 0)
              ? "In stock"
              : `${currentInventory} ${currentInventory === 1 ? "unit" : "units"}`}
          </Chip>
          <Chip
            tone="purple"
            icon={
              showAsOnline ? (
                <Globe className="w-3.5 h-3.5 flex-shrink-0" />
              ) : (
                <MapPin className="w-3.5 h-3.5 flex-shrink-0" />
              )
            }
          >
            {showAsOnline
              ? "Online"
              : storeInfo?.address ||
                [storeInfo?.city, storeInfo?.state].filter(Boolean).join(", ") ||
                (isCostco
                  ? `#${formatStoreId(product.storeId).replace(/-wh$/, "")}`
                  : `#${formatStoreId(product.storeId)}`)}
          </Chip>
          {!showAsOnline && !isDollarGeneral && !isCostco && product.aisle && (
            <Chip tone="purple">Aisle {product.aisle}</Chip>
          )}
        </div>

        {/* Primary actions: same pair as the app's detail sheet. */}
        {(!showAsOnline || canOpenRetailer) && (
          <div className="flex gap-2">
            {!showAsOnline && (
              <CheckStockButton
                productSku={getRetailerLookupSku(product) || product.upc}
                productUpc={product.upc}
                // Same as the list row: close this sheet, then the page's stock results modal opens.
                onSearch={pageCheckStock ? (params) => { onClose(); return pageCheckStock(params); } : undefined}
                label={t("topFeed.checkStock")}
                className="!h-11 sm:!h-10 !flex-1 !justify-center !rounded-xl !border !border-emerald-300 !text-sm !font-semibold dark:!border-emerald-700/50"
              />
            )}
            {canOpenRetailer && (
              <button
                type="button"
                onClick={handleAffiliateClick}
                disabled={affiliateLoading}
                className="flex h-11 flex-1 items-center justify-center gap-2 rounded-xl bg-blue-50 px-4 text-sm font-semibold text-blue-700 transition-colors hover:bg-blue-100 disabled:opacity-60 sm:h-10 dark:bg-blue-900/30 dark:text-blue-300 dark:hover:bg-blue-900/50"
              >
                {affiliateLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ExternalLink className="h-4 w-4" />}
                {getRetailerLinkLabel(retailer)}
              </button>
            )}
          </div>
        )}

        <ResaleCard
          product={product}
          buyPrice={currentPrice}
          units={showAsOnline ? 1 : currentInventory}
          retailer={retailer}
          onUpgrade={() => {
            onClose();
            navigate("/settings?tab=billing&upgradeSource=product_detail_resale");
          }}
        />

        {/* Deal Insights */}
        {detailEntitlements.canViewAdvancedInsights && (product as any).insights && (product as any).insights.score > 0 && (
          <div className="rounded-xl px-3 py-2.5 bg-gray-50/80 dark:bg-dark-800 border border-gray-100 dark:border-dark-700">
            <div className="flex items-center gap-2 mb-1">
              <span className={`text-[13px] font-bold ${
                (product as any).insights.verdict === "BUY_NOW"
                  ? "text-emerald-600"
                  : (product as any).insights.verdict === "GOOD_DEAL"
                  ? "text-emerald-600"
                  : (product as any).insights.verdict === "WAIT"
                  ? "text-yellow-600"
                  : "text-gray-500 dark:text-dark-400"
              }`}>
                {(product as any).insights.verdict === "BUY_NOW" ? "🔥 BUY NOW" :
                 (product as any).insights.verdict === "GOOD_DEAL" ? "✓ Good Deal" :
                 (product as any).insights.verdict === "WAIT" ? "⏳ Wait" : "⚠️ Overpriced"}
              </span>
              <span className="text-[11px] text-gray-400 dark:text-dark-500">•</span>
              <span className="text-xs text-gray-500 dark:text-dark-400">
                Score: <span className="font-semibold text-gray-700 dark:text-dark-300">{(product as any).insights.score}</span>/100
              </span>
            </div>
            <p className="text-xs text-gray-600 dark:text-dark-300 leading-relaxed mb-2">
              {(product as any).insights.reasoning}
            </p>
            <div className="flex items-center flex-wrap gap-x-3 gap-y-1 text-[11px] text-gray-500 dark:text-dark-400 pt-1.5 border-t border-gray-100 dark:border-dark-700">
              <span>Low: <span className="font-semibold text-gray-700 dark:text-dark-300">{formatCurrency((product as any).insights.historicalLow)}</span></span>
              <span>High: <span className="font-semibold text-gray-700 dark:text-dark-300">{formatCurrency((product as any).insights.historicalHigh)}</span></span>
              <span>{(product as any).insights.daysTracked}d tracked</span>
            </div>
          </div>
        )}

        {!showAsOnline && (
          <OtherStoreAvailability
            productIdentifier={sku}
            currentStoreId={product.storeId}
            retailer={retailer}
          />
        )}

        {product?.productKey && isAdmin && (
          <div className="flex flex-col sm:flex-row gap-2">
            {isAdmin && (
              <button
                onClick={handleSendPush}
                disabled={pushLoading}
                className="flex sm:flex-1 items-center justify-center h-12 sm:h-10 px-4 bg-rose-600 text-white rounded-xl hover:bg-rose-700 active:scale-[0.99] transition-all text-sm font-semibold disabled:opacity-60"
              >
                {pushLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <BellRing className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-2" />}
                Send Push
              </button>
            )}
            {isAdmin && (
              <button
                onClick={handleCreateSocialPost}
                disabled={socialPostLoading}
                className="flex sm:flex-1 items-center justify-center h-12 sm:h-10 px-4 bg-sky-600 text-white rounded-xl hover:bg-sky-700 active:scale-[0.99] transition-all text-sm font-semibold disabled:opacity-60"
              >
                {socialPostLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Share2 className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-2" />}
                Social Post
              </button>
            )}
            {isAdmin && isWalmart && (
              <button
                onClick={handleAddAsAnnouncement}
                disabled={announcementLoading}
                className="flex sm:flex-1 items-center justify-center h-12 sm:h-10 px-4 bg-amber-600 text-white rounded-xl hover:bg-amber-700 active:scale-[0.99] transition-all text-sm font-semibold disabled:opacity-60"
              >
                {announcementLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Megaphone className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-2" />}
                Add as Announcement
              </button>
            )}
          </div>
        )}

        <ProductRecommendations
          sku={sku}
          storeId={showAsOnline ? "online" : product.storeId}
          retailer={retailer}
        />

        {/* Price & Stock History Chart */}
        {detailEntitlements.canViewHistory && priceHistoryData.length > 1 && (
          <div ref={chartRef}>
            <PriceStockChart data={priceHistoryData} isOnlineRetailer={showAsOnline} />
          </div>
        )}

        {(!isFreeUser || showAsOnline) && (
          <div className="bg-gray-50/80 dark:bg-dark-800 border border-gray-100 dark:border-dark-700 rounded-xl px-3 py-2.5">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <span className="text-[10px] font-bold text-gray-400 dark:text-dark-400 block mb-0.5 uppercase tracking-wider">
                  {isPotentialPennyItem ? t("products.firstSpottedPenny") : t("products.lastChanged")}
                </span>
                <span className="text-xs font-medium text-gray-700 dark:text-dark-200">
                  {isPotentialPennyItem ? pennyFirstSpottedLabel : lastChangeLabel}
                </span>
              </div>
              <div>
                <span className="text-[10px] font-bold text-gray-400 dark:text-dark-400 block mb-0.5 uppercase tracking-wider">
                  {isPotentialPennyItem ? t("products.lastSeenPenny") : t("products.lastUpdatedLabel")}
                </span>
                <span className="text-xs font-medium text-gray-700 dark:text-dark-200">
                  {isPotentialPennyItem ? pennyLastRegularLabel : lastUpdatedLabel}
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Product Codes */}
        {canViewProductCodes && (
          <div className="bg-gray-50/80 dark:bg-dark-800 border border-gray-100 dark:border-dark-700 rounded-xl px-3 py-2.5">
            <div className="text-[10px] font-bold text-gray-400 dark:text-dark-400 mb-1.5 uppercase tracking-wider">
              {t("products.productCode")}
            </div>
            <div className="flex flex-wrap gap-x-3 gap-y-1.5">
              {isValidCode(product.upc) && (
                <div className="flex items-center gap-1.5">
                  <span className="text-xs text-gray-600 dark:text-dark-400">
                    {isHomeDepot ? "Item ID" : isTarget ? "TCIN" : "SKU"}:
                  </span>
                  <span className="text-xs font-mono text-gray-900 dark:text-white">
                    {product.upc}
                  </span>
                  <button
                    onClick={() => copyToClipboard(product.upc, isHomeDepot ? "Item ID" : isTarget ? "TCIN" : "SKU")}
                    className="p-1 text-gray-400 hover:text-gray-600 dark:text-dark-500 dark:hover:text-dark-300 hover:bg-gray-200 dark:hover:bg-dark-700 rounded transition-colors"
                  >
                    <Copy className="w-3 h-3" />
                  </button>
                </div>
              )}
              {!isTarget && !isBestBuy && !isOnlineRetailer && isValidCode(product.sku) && (
                <div className="flex items-center gap-1.5">
                  <span className="text-xs text-gray-600 dark:text-dark-400">UPC:</span>
                  <span className="text-xs font-mono text-gray-900 dark:text-white">{product.sku}</span>
                  <button
                    onClick={() => copyToClipboard(product.sku, "UPC")}
                    className="p-1 text-gray-400 hover:text-gray-600 dark:text-dark-500 dark:hover:text-dark-300 hover:bg-gray-200 dark:hover:bg-dark-700 rounded transition-colors"
                  >
                    <Copy className="w-3 h-3" />
                  </button>
                </div>
              )}
            </div>
            {!isTarget && !isBestBuy && !isOnlineRetailer && isValidCode(product.sku) && (
              <div className="flex justify-center mt-2.5">
                <BarcodeComponent
                  upc={product.sku}
                  width={1.5}
                  height={50}
                  fontSize={10}
                  displayValue={true}
                  clickable={false}
                  className="bg-white dark:bg-dark-700 border border-gray-200 dark:border-dark-600 rounded-md p-2"
                />
              </div>
            )}
            {isTarget && isValidCode(product.upc) && (
              <div className="flex justify-center mt-2.5">
                <BarcodeComponent
                  upc={String(product.upc)}
                  width={1.5}
                  height={50}
                  fontSize={10}
                  displayValue={true}
                  clickable={false}
                  forceCode128={true}
                  className="bg-white dark:bg-dark-700 border border-gray-200 dark:border-dark-600 rounded-md p-2"
                />
              </div>
            )}
          </div>
        )}
      </div>

      {/* Expanded product image lightbox */}
      {imageExpanded &&
        product.image &&
        createPortal(
          <div
            className="fixed inset-0 z-[10050] flex items-center justify-center bg-black/80 p-4 sm:p-10 cursor-zoom-out"
            onClick={() => setImageExpanded(false)}
          >
            <button
              type="button"
              onClick={() => setImageExpanded(false)}
              className="absolute top-4 right-4 p-2 rounded-full bg-white/10 text-white hover:bg-white/25 transition-colors"
              aria-label="Close image"
            >
              <X className="w-6 h-6" />
            </button>
            <img
              src={getProductImageUrl(product.image, "1200")}
              alt={product.name}
              className="max-w-full max-h-full object-contain rounded-2xl bg-white p-3 sm:p-6 shadow-2xl"
            />
          </div>,
          document.body
        )}
    </div>
  );
}
